/**
 * Reports (installer-owned): snapshots — Availability and Alerts & Incidents.
 * Generate → list → view → download. No report builder, no custom SQL.
 */
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Loader2, Download, FileText } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import {
  useReports,
  useGenerateReport,
  fetchReportArtifacts,
  type ReportRow,
  type ReportArtifactRow,
} from '@/hooks/useReports';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const REPORT_TYPE_LABEL: Record<string, string> = {
  availability: 'System Availability',
  alerts_incidents: 'Alerts & Incidents',
};

const DATE_PRESETS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];
const CUSTOM_PRESET = 0;

function paramsSummary(params: ReportRow['params']): string {
  const from = params?.date_from ?? '—';
  const to = params?.date_to ?? '—';
  const scope = params?.scope === 'selected' ? 'Selected properties' : 'All properties';
  return `${from} to ${to} · ${scope}`;
}

export default function Reports() {
  const { properties, isInstaller, navigationScope } = useApp();
  const scopedPropertyId = navigationScope.type === 'property' ? navigationScope.propertyId : undefined;
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [detailReportId, setDetailReportId] = useState<string | null>(null);
  const [detailArtifacts, setDetailArtifacts] = useState<ReportArtifactRow[] | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [genType, setGenType] = useState<'availability' | 'alerts_incidents'>('availability');
  const [genPreset, setGenPreset] = useState<number | typeof CUSTOM_PRESET>(30);
  const [genCustomFrom, setGenCustomFrom] = useState('');
  const [genCustomTo, setGenCustomTo] = useState('');
  const [genScope, setGenScope] = useState<'all' | 'selected'>(scopedPropertyId ? 'selected' : 'all');
  const [genPropertyIds, setGenPropertyIds] = useState<string[]>(scopedPropertyId ? [scopedPropertyId] : []);

  const { reports, isLoading: reportsLoading, refetch: refetchReports } = useReports();
  const generateReport = useGenerateReport();

  const openDetail = async (reportId: string) => {
    setDetailReportId(reportId);
    setDetailArtifacts(null);
    setDetailLoading(true);
    try {
      const arts = await fetchReportArtifacts(reportId);
      setDetailArtifacts(arts);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load report');
      setDetailReportId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleGenerate = async () => {
    let dateFrom: string;
    let dateTo: string;
    if (genPreset === CUSTOM_PRESET && genCustomFrom && genCustomTo) {
      dateFrom = genCustomFrom;
      dateTo = genCustomTo;
    } else {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - (genPreset === CUSTOM_PRESET ? 30 : genPreset));
      dateFrom = start.toISOString().slice(0, 10);
      dateTo = end.toISOString().slice(0, 10);
    }
    if (dateFrom > dateTo) {
      toast.error('Start date must be before end date');
      return;
    }
    try {
      await generateReport.mutateAsync({
        type: genType,
        date_from: dateFrom,
        date_to: dateTo,
        scope: genScope,
        property_ids: genScope === 'selected' ? genPropertyIds : undefined,
      });
      toast.success('Report generated');
      setGeneratorOpen(false);
      refetchReports();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to generate report');
    }
  };

  const downloadCsv = (contentText: string | null, reportId: string, type: string) => {
    if (!contentText) return;
    const blob = new Blob([contentText], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${type}-${reportId.slice(0, 8)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isInstaller) {
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Reports</h1>
        <p className="text-muted-foreground">
          Generate and download System Availability and Alerts & Incidents snapshots.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Generated reports</CardTitle>
            <CardDescription>Snapshots are immutable. View or download CSV.</CardDescription>
          </div>
          <Button onClick={() => setGeneratorOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Generate report
          </Button>
        </CardHeader>
        <CardContent>
          {reportsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : reports.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center">No reports yet. Generate one to get started.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Date range & scope</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead className="w-[140px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{REPORT_TYPE_LABEL[r.type] ?? r.type}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{paramsSummary(r.params)}</TableCell>
                    <TableCell>{formatDistanceToNow(new Date(r.generated_at), { addSuffix: true })}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openDetail(r.id)}>
                          <FileText className="mr-1 h-4 w-4" />
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={generatorOpen} onOpenChange={setGeneratorOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate report</DialogTitle>
            <DialogDescription>
              Choose report type, date range, and scope. Report is generated and stored for download.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Report type</Label>
              <Select value={genType} onValueChange={(v) => setGenType(v as 'availability' | 'alerts_incidents')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="availability">System Availability</SelectItem>
                  <SelectItem value="alerts_incidents">Alerts & Incidents</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Date range</Label>
              <Select
                value={genPreset === CUSTOM_PRESET ? 'custom' : String(genPreset)}
                onValueChange={(v) => setGenPreset(v === 'custom' ? CUSTOM_PRESET : Number(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map((p) => (
                    <SelectItem key={p.days} value={String(p.days)}>
                      {p.label}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
              {genPreset === CUSTOM_PRESET && (
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={genCustomFrom}
                    onChange={(e) => setGenCustomFrom(e.target.value)}
                    placeholder="From"
                  />
                  <Input
                    type="date"
                    value={genCustomTo}
                    onChange={(e) => setGenCustomTo(e.target.value)}
                    placeholder="To"
                  />
                </div>
              )}
            </div>
            <div className="grid gap-2">
              <Label>Scope</Label>
              <Select value={genScope} onValueChange={(v) => setGenScope(v as 'all' | 'selected')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All properties</SelectItem>
                  <SelectItem value="selected">Selected properties</SelectItem>
                </SelectContent>
              </Select>
              {genScope === 'selected' && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {properties.map((p) => (
                    <label key={p.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={genPropertyIds.includes(p.id)}
                        onCheckedChange={(checked) => {
                          setGenPropertyIds((ids) =>
                            checked ? [...ids, p.id] : ids.filter((id) => id !== p.id)
                          );
                        }}
                      />
                      <span className="text-sm">{p.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGeneratorOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerate} disabled={generateReport.isPending}>
              {generateReport.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(detailReportId)} onOpenChange={(open) => !open && setDetailReportId(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Report detail</DialogTitle>
            <DialogDescription>Preview and download CSV.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto min-h-0">
            {detailLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : detailArtifacts && detailArtifacts.length > 0 ? (
              <div className="space-y-4">
                {detailArtifacts.map((art) => (
                  <div key={art.id}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{art.format.toUpperCase()}</span>
                      {art.content_text && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            downloadCsv(art.content_text, detailReportId!, art.format)
                          }
                        >
                          <Download className="mr-1 h-4 w-4" />
                          Download CSV
                        </Button>
                      )}
                    </div>
                    {art.content_text && (
                      <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-60 whitespace-pre-wrap font-mono">
                        {art.content_text.slice(0, 3000)}
                        {art.content_text.length > 3000 ? '\n…' : ''}
                      </pre>
                    )}
                    {art.content_json && typeof art.content_json === 'object' && 'summary' in art.content_json && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Summary: {JSON.stringify((art.content_json as { summary: unknown }).summary)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground py-6 text-center">No artifacts for this report.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
